import { formatDuration, izmeneAcc, minutesToHm, zonedNow } from '@reditelji/shared';
import type { ChangelogEntry } from '../api/types';
import type { CancelPlan, CancelRecord } from '../api/volunteers';
import { plIzmen } from './derive';

/**
 * The words of the "Ne pride" dialog and its strip (SPEC §1.1K), kept apart
 * from the component so the Slovenian numerals can be tested.
 */

/** The dialog's primary button: what pressing it frees. */
export function commitLabel(n: number): string {
  return n === 0 ? 'Zabeleži' : `Sprosti ${izmeneAcc(n)}`;
}

/** "sproščene 3 izmene" — what the strip says after a cancel. */
export function freedPhrase(n: number): string {
  if (n === 0) return 'nobena izmena ni sproščena';
  const m = n % 100;
  const [adj, noun] =
    m === 1
      ? ['sproščena', 'izmena']
      : m === 2
        ? ['sproščeni', 'izmeni']
        : m === 3 || m === 4
          ? ['sproščene', 'izmene']
          : ['sproščenih', 'izmen'];
  return `${adj} ${n} ${noun}`;
}

/** "sobota 19. 9." from a day's label and its ISO date. */
export function dayHeading(label: string, date: string): string {
  const [, month, day] = date.split('-');
  return `${label.toLowerCase()} ${Number(day)}. ${Number(month)}.`;
}

export interface PlanRow {
  shiftId: string;
  postCode: string;
  postMicro: string;
  urgent: boolean;
  heading: string;
  date: string;
  fromMin: number;
  toMin: number;
  outcome: 'free' | 'keep';
  /** What the coordinator needs beside the outcome: how soon, or why it stays. */
  note: string;
  /** A post that is empty right now. */
  hot: boolean;
}

/** Every shift the person holds, with what the cancel does to it, by day and time. */
export function planRows(plan: CancelPlan, now = new Date()): PlanRow[] {
  const today = zonedNow(now);
  const free: PlanRow[] = plan.free.map((f) => {
    // Read against the clock as well as the plan: a shift can start while the
    // dialog is open, and must not read "čez -3 min".
    const running = f.running || (f.date === today.isoDate && f.fromMin <= today.minutes);
    return {
      shiftId: f.shiftId,
      postCode: f.postCode,
      postMicro: f.postMicro,
      urgent: f.urgent,
      heading: dayHeading(f.dayLabel, f.date),
      date: f.date,
      fromMin: f.fromMin,
      toMin: f.toMin,
      outcome: 'free' as const,
      note: running
        ? `teče od ${minutesToHm(f.fromMin)} · ni prijave`
        : f.date === today.isoDate
          ? `čez ${formatDuration(f.fromMin - today.minutes)}`
          : '',
      hot: running,
    };
  });
  const keep: PlanRow[] = plan.keep.map((k) => ({
    shiftId: k.shiftId,
    postCode: k.postCode,
    postMicro: k.postMicro,
    urgent: false,
    heading: dayHeading(k.dayLabel, k.date),
    date: k.date,
    fromMin: k.fromMin,
    toMin: k.toMin,
    outcome: 'keep',
    note: k.label,
    hot: false,
  }));
  return [...free, ...keep].sort((a, b) => a.date.localeCompare(b.date) || a.fromMin - b.fromMin);
}

export interface CancelFact {
  icon: string;
  tone: string;
  text: string;
}

/** Singular, dual and plural: Slovenian counts two of something apart. */
function urgentLine(codes: string[]): string {
  if (codes.length === 1) {
    return `${codes[0]} je nujno mesto — ostane brez osebe, dokler ga ne pokriješ.`;
  }
  if (codes.length === 2) {
    return `${codes.join(' in ')} sta nujni mesti — ostaneta brez osebe, dokler ju ne pokriješ.`;
  }
  return `${codes.join(', ')} so nujna mesta — ostanejo brez osebe, dokler jih ne pokriješ.`;
}

/** What is worth knowing before committing, most urgent first. */
export function cancelFacts(plan: CancelPlan): CancelFact[] {
  const out: CancelFact[] = [];
  for (const f of plan.free) {
    if (f.running) {
      out.push({
        icon: '▲',
        tone: 'var(--rr-danger)',
        text: `${f.postCode} teče zdaj brez osebe — zamenjavo poišči takoj.`,
      });
    }
  }
  // Once per post: two freed shifts at one post are still one post to cover.
  const urgent = [...new Set(plan.free.filter((f) => f.urgent && !f.running).map((f) => f.postCode))];
  if (urgent.length) out.push({ icon: '▲', tone: 'var(--rr-danger)', text: urgentLine(urgent) });
  for (const k of plan.keep) {
    // Standing there now and about to leave: the one shift the cancel cannot
    // free, and the one that needs shortening by hand.
    if (k.reason.kind === 'checked-in' && k.reason.running && k.reason.outMin === null) {
      out.push({
        icon: '▲',
        tone: 'var(--rr-warn)',
        text: `${k.postCode} teče, prijava ob ${minutesToHm(k.reason.inMin)} — na časovnici jo skrajšaj do zdaj in dodaj zamenjavo.`,
      });
    }
  }
  // What the volunteer's page will say is publication, which — like the
  // shared address below — the plan carries only for a viewer with comms:read
  // (SPEC §1.1L). Left out rather than guessed: without it, "not published
  // yet" would be said of everyone.
  if (plan.hasPin !== undefined) {
    out.push({
      icon: '✉',
      tone: 'var(--rr-text-muted)',
      text: !plan.hasPin
        ? 'Razpored še ni bil objavljen; povezava bo pokazala, da tokrat ne pride. Sporočila ne pošljemo.'
        : plan.keep.length
          ? 'Povezava pokaže, da tokrat ne pride, in le izmene, ki ostanejo — brez gumbov za potrditev. Sporočila ne pošljemo.'
          : 'Povezava pokaže, da tokrat ne pride — brez izmen in brez gumbov za potrditev. Sporočila ne pošljemo.',
    });
  }
  for (const s of plan.siblings ?? []) {
    out.push({
      icon: '@',
      tone: 'var(--rr-accent)',
      text: `Isti e-naslov uporablja tudi ${s.name}${s.freeCount ? ` (${plIzmen(s.freeCount)})` : ''}. Če tudi ta oseba ne pride, jo označi posebej.`,
    });
  }
  return out;
}

/** "10. 9." — the day somebody was marked as not coming, on the event's clock. */
export function recordedOn(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString('sl-SI', {
        day: 'numeric',
        month: 'numeric',
        timeZone: 'Europe/Ljubljana',
      })
    : '';
}

/** "10. 9. 14:05" — the same, where the hour matters. */
export function recordedAt(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleString('sl-SI', {
        day: 'numeric',
        month: 'numeric',
        hour: '2-digit',
        hourCycle: 'h23',
        minute: '2-digit',
        timeZone: 'Europe/Ljubljana',
      })
    : '';
}

/** The card's line for freed slots deleted since the mark. */
export function deletedSince(n: number): string {
  const m = n % 100;
  if (m === 1) return `${n === 1 ? 'Ena' : n} sproščena izmena je bila medtem izbrisana.`;
  if (m === 2) return `${n} sproščeni izmeni sta bili medtem izbrisani.`;
  if (m === 3 || m === 4) return `${n} sproščene izmene so bile medtem izbrisane.`;
  return `${n} sproščenih izmen je bilo medtem izbrisanih.`;
}

type RecordShift = CancelRecord['freed'][number];

export interface FreedStanding {
  /** free: open and can go back · taken: somebody holds it · past: open, but over · gone: no longer a slot */
  kind: 'free' | 'taken' | 'past' | 'gone';
  text: string;
}

/** Where a shift the cancel freed stands now, in the Ne pride card's words. */
export function freedStanding(f: RecordShift, returnable: string[]): FreedStanding {
  if (f.now.volunteerId !== null) return { kind: 'taken', text: `zdaj: ${f.now.name ?? '—'}` };
  if (f.now.postDeleted) return { kind: 'gone', text: 'mesto izbrisano' };
  if (f.now.state !== 'open') return { kind: 'gone', text: f.now.state === 'cancelled' ? 'odpovedana' : 'ni več odprta' };
  if (returnable.includes(f.shiftId)) return { kind: 'free', text: 'PROSTO' };
  return { kind: 'past', text: 'ostala prosta · že mimo' };
}

/**
 * What "Vrni in dodeli nazaj" will do, said before it is pressed. `carries` is
 * the server's own answer to whether the undo keeps their answer, so the
 * promise and the outcome are one rule.
 */
export function comeBackHint(rec: Pick<CancelRecord, 'freedTotal' | 'returnable' | 'carries'>): string {
  const back = rec.returnable.length;
  if (rec.carries) {
    return rec.freedTotal === 0
      ? 'Nič ni bilo sproščeno — potrditev ostane, kot je bila.'
      : 'Vse izmene nazaj, kot so bile — potrditev ostane, kot je bila.';
  }
  if (rec.freedTotal === 0) return 'Razpored se je medtem spremenil — gre v ponovno potrditev.';
  if (back === 0) return 'Nobena sproščena izmena ni več prosta — oseba se vrne brez izmen in razpored čaka potrditev.';
  if (back === rec.freedTotal) return 'Vse izmene nazaj, a razpored se je medtem spremenil — gre v ponovno potrditev.';
  // The sprememba diffs against the empty page the cancel published, so what
  // did not come back is never mentioned (SPEC §1.1K).
  return 'Le del izmen nazaj — razpored gre v ponovno potrditev. Obvestilo o spremembi bo naštelo le izmene, ki se vrnejo.';
}

/** The notification after "Vendarle pride…". */
export function comeBackDone(res: {
  restored: string[];
  notBack: unknown[];
  outcome: 'carried' | 'changed' | 'empty' | 'noop';
}): string {
  switch (res.outcome) {
    case 'noop':
      return 'Oseba je že na seznamu.';
    case 'carried':
      return res.restored.length
        ? 'Vendarle pride — izmene so vrnjene, potrditev ostane, kot je bila.'
        : 'Vendarle pride — potrditev ostane, kot je bila.';
    case 'changed':
      return `Vendarle pride · vrnjeno: ${plIzmen(res.restored.length)}. Razpored čaka ponovno potrditev.`;
    case 'empty':
      return 'Vendarle pride · brez izmen.';
  }
}

/**
 * The "Ne pride" that left a slot open, while that is still why it is open:
 * the newest entry that moved the slot's holder, when it is a `freed`. A later
 * assignment, a hand-made Sprosti or a return all answer null.
 */
export function latestFreed(
  entries: ChangelogEntry[],
): { at: string; volunteerId: string; name: string } | null {
  const moves = entries
    .filter((e) => {
      const after = e.after;
      return (
        e.kind === 'freed' ||
        e.kind === 'restored' ||
        (typeof after === 'object' && after !== null && 'volunteerId' in after)
      );
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const e = moves[0];
  if (!e || e.kind !== 'freed') return null;
  const before = (e.before ?? {}) as { volunteerId?: unknown; name?: unknown };
  if (typeof before.volunteerId !== 'string') return null;
  return {
    at: e.createdAt,
    volunteerId: before.volunteerId,
    name: typeof before.name === 'string' ? before.name : '',
  };
}
